import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import { IProjectAOI } from "../../interfaces/i-project-aoi";
import { Viewerlayer } from "../../model/viewerlayer";
import { IHistoryProject } from "../../interfaces/i-history-project";
import { ProjectAOI } from "../../model/project-aoi";
import { IMetadataImage } from "../../interfaces/i-metadataimage";
import { ViewerConstants } from "../../../shared/model/viewer-constants";
import { ImagesService } from "./images.service";
import { Collection } from "../../model/collection";
import { ICollection } from "../../interfaces/i-collection";

function sortByDate(aois: IProjectAOI[]): IProjectAOI[] {
  return aois.sort((a1, a2) => a2.maxDate.getTime() - a1.maxDate.getTime());
}
@Injectable({
  providedIn: "root",
})
export class ProjectService {
  private projectsSubject = new Subject<Viewerlayer[]>();

  public projects$ = this.projectsSubject.asObservable();

  /**
   * Update the information of the projects with the image information
   * @param layers
   * @param projects
   * @deprecated
   */
  static populateImageProjects(layers: Viewerlayer[], projects: IHistoryProject[]) {
    return layers?.map((layer) => {
      let layerProjects = []; //this force to remove previous projects
      if (projects?.length) {
        layerProjects = projects
          .filter((prj) => layer.layer === prj.layer)
          .sort((p1, p2) => p2.maxDate.getTime() - p1.maxDate.getTime());
        layerProjects.forEach((prj) => {
          prj.layerName = layer.name;
          return prj;
        });
      }
      layer.projects = layerProjects;
      layer.aois = ProjectService.populateImageProjects(layer.aois, projects);

      return layer;
    });
  }

  /**
   * Get layer projects (collection and child aois) from list of layers
   * Sort by date
   * @param layers
   * @param image
   * @deprecated iterate over collections to get aois, bad performance
   */
  static parseProjects(layers: Viewerlayer[], image = false): IProjectAOI[] {
    if (!layers || !layers.length) {
      return [];
    }

    // remove this pass like parameters
    const aois = layers
      .filter((collection) => collection.aois)
      .reduce((previousValue, currentValue) => previousValue.concat(currentValue.aois), [])
      .reduce(
        (previousValue, currentValue) =>
          currentValue.aois?.length ? previousValue.concat(currentValue.aois) : previousValue.concat([currentValue]),

        [],
      );

    return [...layers.filter((collection) => !collection.aois), ...aois]
      .filter((project) => project.projects)
      .reduce((acc, project) => {
        let minDate;
        let maxDate;
        let imageProject;

        // create project for each aoi layer
        if (project.aoi) {
          let prjs = [...project.projects];
          project.projects
            .filter((prj) => prj.children?.length)
            .map((prj) => {
              prjs = prjs.concat(prj.children);
            });
          imageProject = prjs.find((prj) => prj.code === project.aoi);
          minDate = new Date(project.min_date);
          maxDate = new Date(project.max_date || project.min_date);

          let orientations = [];
          if (imageProject) {
            const childAOI = imageProject.children?.find((cAoi) => cAoi.code === project.aoi);
            if (imageProject && childAOI) {
              orientations = childAOI.orientations;
            } else {
              orientations = imageProject.orientations;
            }
          }
          const images = imageProject ? imageProject.images : [];
          const averageGsd = project.avg_gsd ? project.avg_gsd : (project.max_gsd + project.min_gsd) / 2;
          const aoiOblique = project.aoiOblique ? project.aoiOblique : false;
          const newestNadirDate =
            ImagesService.getNewestImage(images, ViewerConstants.DISCRETE_ORIENTATIONS.NADIR)?.capturedDate || maxDate;
          const name = imageProject ? imageProject.code : project.aoi;
          return acc.concat([
            ProjectAOI(
              name,
              null,
              project,
              minDate,
              maxDate,
              project.max_zoom,
              averageGsd,
              aoiOblique,
              newestNadirDate,
              project.collection_name,
              project.collectionType,
              orientations,
              images,
            ),
          ]);
        }
        if (!image) {
          // create project for ortho layer without aoi
          const firstProject = project.projects[0] as IHistoryProject;
          imageProject = firstProject || null;
          minDate = firstProject.minDate;
          maxDate = firstProject.maxDate;
          const orientations = imageProject ? imageProject.orientations : [];
          const images = imageProject ? imageProject.images : [];
          const averageGsd = project.avg_gsd ? project.avg_gsd : (project.max_gsd + project.min_gsd) / 2;
          const aoiOblique = project.aoiOblique ? project.aoiOblique : false;
          const newestNadirDate =
            ImagesService.getNewestImage(images, ViewerConstants.DISCRETE_ORIENTATIONS.NADIR)?.capturedDate || maxDate;
          const name = imageProject ? imageProject.code : project.aoi;
          return acc.concat([
            ProjectAOI(
              name,
              name,
              project,
              minDate,
              maxDate,
              project.max_zoom,
              averageGsd,
              aoiOblique,
              newestNadirDate,
              project.name,
              project.collectionType,
              orientations,
              images,
            ),
          ]);
        }
        // create image projects for layer without aois
        if (image && project.projects?.length) {
          const imageProjects = project.projects.map((prj) => {
            minDate = prj.minDate;
            maxDate = prj.maxDate;
            const orientations = prj ? prj.orientations : [];
            const images = prj ? prj.images : [];
            const averageGsd = project.avg_gsd ? project.avg_gsd : (project.max_gsd + project.min_gsd) / 2;
            const aoiOblique = project.aoiOblique ? project.aoiOblique : false;
            const newestNadirDate =
              ImagesService.getNewestImage(images, ViewerConstants.DISCRETE_ORIENTATIONS.NADIR)?.capturedDate ||
              maxDate;
            return ProjectAOI(
              prj.code,
              prj.code,
              project,
              minDate,
              maxDate,
              project.max_zoom,
              averageGsd,
              aoiOblique,
              newestNadirDate,
              project.name,
              project.collectionType,
              orientations,
              images,
            );
          });

          return acc.concat(imageProjects);
        }
        return acc;
      }, []);
  }

  /**
   * Process collection projects (aois) for current images
   * Process each aoi with image data, project date
   * Calculate gsd, newest image etc
   * @param layer
   * @param enabled
   * @deprecated
   * Implementation that used old collection layers logic
   */
  static processProject(layer: Viewerlayer, enabled: boolean): IProjectAOI {
    let minDate: Date;
    let maxDate: Date;
    let imageProject: IHistoryProject;
    let orientations = [];
    let images = [];
    let name: string;
    let newestNadirDate: Date;
    // create project for each aoi layer
    if (layer.aoi) {
      name = layer.aoi;
      minDate = new Date(layer.min_date);
      maxDate = new Date(layer.max_date || layer.min_date);
      newestNadirDate = maxDate;
      //we have image projects for this aois, we try to get data from images
      if (layer.projects) {
        let prjs = layer.projects ? [...layer.projects] : [];
        layer.projects
          .filter((prj) => prj.children?.length)
          .map((prj) => {
            prjs = prjs.concat(prj.children);
          });
        imageProject = prjs.find((prj) => prj.code === layer.aoi);
        if (imageProject) {
          images = imageProject.images;
          name = imageProject.code;
          newestNadirDate = ImagesService.getNewestImage(images, ViewerConstants.DISCRETE_ORIENTATIONS.NADIR)
            ?.capturedDate;
          // sometimes we dont have nadir orientation, can we get layer max date or first image
          if (!newestNadirDate) {
            newestNadirDate = images[0].capturedDate;
          }
          const childAOI = imageProject.children?.find((cAoi) => cAoi.code === layer.aoi);
          if (imageProject && childAOI) {
            orientations = childAOI.orientations;
          } else {
            orientations = imageProject.orientations;
          }
        }
      }

      const averageGsd = layer.avg_gsd ? layer.avg_gsd : (layer.max_gsd + layer.min_gsd) / 2;
      const aoiOblique = layer.aoiOblique ? layer.aoiOblique : false;

      return ProjectAOI(
        name,
        null,
        layer,
        minDate,
        maxDate,
        layer.max_zoom,
        averageGsd,
        aoiOblique,
        newestNadirDate,
        layer.collection_name,
        layer.collectionType,
        orientations,
        images,
        enabled,
      );
    }
    return null;
  }

  static processCollection(imageProject: IHistoryProject, collection: ICollection, enabled: boolean): IProjectAOI {
    let minDate: Date = new Date(collection.properties["first-capture-date"]);
    let maxDate: Date = new Date(collection.properties["last-capture-date"]);
    let newestNadirDate: Date = maxDate;

    let orientations = [];
    let images = [];

    const averageGsd =
      collection.properties["avg-gsd"] || (collection.properties["max-gsd"] + collection.properties["min-gsd"]) / 2;
    const aoiOblique = collection.oblique || false;
    const collectionType = collection.properties.layer.includes(ViewerConstants.COLLECTION_PREFIX.GRAYSKY)
      ? ViewerConstants.COLLECTION_PREFIX.GRAYSKY
      : ViewerConstants.COLLECTION_PREFIX.BLUESKY;

    if (imageProject) {
      newestNadirDate = ImagesService.getNewestImage(imageProject.images, ViewerConstants.DISCRETE_ORIENTATIONS.NADIR)
        ?.capturedDate;
      // sometimes we dont have nadir orientation, can we get layer max date or first image
      if (!newestNadirDate) {
        newestNadirDate = imageProject.images[0].capturedDate;
      }
      orientations = imageProject.orientations;
      images = imageProject.images;
    }

    const cirAvailable =
      collection.properties.bands != null &&
      Array.isArray(collection.properties.bands) &&
      collection.properties.bands.length > 0 &&
      collection.properties.bands.includes("irg");

    const layerLabel = ProjectService.layerName(collection.properties.layer);
    return Collection(
      collection.properties.collection,
      collection.properties["pretty-name"],
      null,
      minDate,
      maxDate,
      collection.properties["max-zoom"],
      averageGsd,
      aoiOblique,
      newestNadirDate,
      layerLabel,
      collectionType,
      orientations,
      images,
      collection.geometry,
      enabled,
      collection.properties.layer,
      collection.properties["source-layer"],
      collection.properties["product-type"],
      null,
      cirAvailable,
    );
  }

  static visibleProject(projects: IProjectAOI[]): IProjectAOI {
    return ProjectService.visibleProjects(projects)[0];
  }

  static visibleProjects(projects: IProjectAOI[]): IProjectAOI[] {
    return [...projects].filter((aoi) => aoi.isVisible()).sort((c1, c2) => c2.getOrder() - c1.getOrder());
  }

  static imageProject(projects: IProjectAOI[], img: IMetadataImage): IProjectAOI {
    if (!projects?.length || !img) {
      return null;
    }
    let project = projects.find((prj) => prj.hasImage(img.image_id));
    if (!project) {
      project = projects.find((prj) => prj.getName() === img.projectname || prj.getParentAOI() === img.projectname);
    }
    if (!project) {
      project = projects.find((prj) => {
        return prj.getLayer() === img.layername;
      });
    }
    return project;
  }

  /**
   * Transform project list in map of list of projects grouped by layer name
   * @param projects
   */
  static groupByLayer(projects: IProjectAOI[]): Map<string, IProjectAOI[]> {
    const map = new Map<string, IProjectAOI[]>();
    projects.forEach((item) => {
      const layer = item.getLayer();
      if (map.has(layer)) {
        map.get(layer)!.push(item);
      } else {
        map.set(layer, [item]);
      }
    });

    return map;
  }

  static findUrban(aois: IProjectAOI[]): IProjectAOI[] {
    return sortByDate(aois.filter((aoi) => aoi.isUrban()));
  }

  static findGraySky(aois: IProjectAOI[]): IProjectAOI[] {
    return sortByDate(aois.filter((aoi) => aoi.isGraySky()));
  }

  static findWide(aois: IProjectAOI[]): IProjectAOI[] {
    return sortByDate(aois.filter((aoi) => aoi.isWide()));
  }

  static findIR(aois: IProjectAOI[]): IProjectAOI[] {
    return sortByDate(aois.filter((aoi) => aoi.hasInfrared()));
  }

  /**
   * Maps a layer name to its corresponding collection name.
   * @param layerName The value from LAYER_NAMES.
   * @returns The corresponding value from COLLECTION_NAMES, or null if not found.
   */
  static layerName(layerName: string): string | null {
    // Find the key in LAYER_NAMES that matches the given layerName
    const key = Object.keys(ViewerConstants.LAYER_SOURCE).find((k) => ViewerConstants.LAYER_SOURCE[k] === layerName);

    // Use the found key to retrieve the value from COLLECTION_NAMES
    return key ? ViewerConstants.LAYER_LABELS[key] : null;
  }
}
